/** Regal-Detail (Screen laden/Regal-Detail): Regaldaten, Belegung, aktuelle Mieter, Buchungen. Leistung/Warteliste/QR-Schild sind V2. */
import { useState } from "react";
import { Link, useParams } from "react-router";
import { Button, EmptyState, Seitenzustand, StatusBadge } from "../ui";
import { Card, Dl, PageHeader, Split } from "../ui/Layout";
import { formatEuro } from "@shared/geld";
import { formatTag, tagVon } from "@shared/zeit";
import { tagPlus } from "@shared/perioden";
import { useRegal } from "./regale-api";
import { BELEGUNG_TEXT, klassenPreisText, RegalFormular } from "./Regale";
import { BUCHUNG_STATUS } from "./BuchungDetail";
import { useRegalLeistung } from "./geld-v2-api";

export function RegalDetail() {
  const { id = "" } = useParams();
  const regal = useRegal(id);
  const [bearbeiten, setBearbeiten] = useState(false);

  return (
    <Seitenzustand query={regal} lines={8}>
      {(d) => {
        const heute = tagVon();
        const belegungArt = !d.regal.aktiv ? "inaktiv" : d.aktuell ? (d.aktuell.status === "reserviert" ? "reserviert" : d.aktuell.ende && d.aktuell.ende <= tagPlus(heute, 14) ? "bald_frei" : "belegt") : "frei";
        const st = BELEGUNG_TEXT[belegungArt];
        return (
          <>
            <PageHeader
              kicker={<Link to="/laden/regale">Regale</Link>}
              title={`Regal ${d.regal.nummer}`}
              sub={
                <span className="sc-toolbar">
                  <StatusBadge status={st.badge}>{st.text}</StatusBadge>
                  {d.klasse ? <StatusBadge status="neutral">{d.klasse.name}</StatusBadge> : null}
                  <span>{[d.regal.lage, d.aktuell ? `${d.aktuell.mieterName} ${d.aktuell.ende ? `bis ${formatTag(d.aktuell.ende)}` : "(Abo)"}` : d.freiAb ? `frei ab ${formatTag(d.freiAb)}` : null].filter(Boolean).join(" · ")}</span>
                </span>
              }
              actions={
                <>
                  <a href={`/api/laden/regal-schilder.pdf?regalId=${d.regal.id}&format=a6`} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">Schild (A6)</a>
                  <a href={`/api/laden/regal-schilder.pdf?regalId=${d.regal.id}&format=a4`} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">Plakat (A4)</a>
                  <Button variant="secondary" icon="pencil" onClick={() => setBearbeiten(true)}>Bearbeiten</Button>
                  <Link to={`/laden/buchungen/neu?regal=${d.regal.id}${d.freiAb ? `&start=${d.freiAb}` : ""}`} className="rg-btn rg-btn--primary">Buchung anlegen</Link>
                </>
              }
            />
            <Split
              main={
                <>
                  <Card title="Regaldaten">
                    <Dl
                      rows={[
                        ["Größe", d.regal.groesse || d.klasse?.groesseText || "–"],
                        ["Lage", d.regal.lage || "–"],
                        ["Preisklasse", d.klasse ? `${d.klasse.name} · ${klassenPreisText(d.klasse)}` : "keine"],
                        ["Kategorie", d.regal.kategorie || "–"],
                        ["Artikel im Regal", d.artikel.length ? `${d.artikel.reduce((a, x) => a + x.n, 0)} (${d.artikel.map((x) => `${x.mieterName.split(" ")[0]} ${x.n}`).join(" · ")})` : "0"],
                        ["Notiz", d.regal.notiz || "–"],
                      ]}
                    />
                  </Card>
                  <Card title="Buchungen" flush>
                    {d.buchungen.length === 0 ? (
                      <EmptyState compact illustration="empty-shelf" title="Noch keine Buchung">Dieses Regal war noch nie vermietet.</EmptyState>
                    ) : (
                      <div className="app-tabellen-scroll">
                        <table className="rg-table">
                          <thead><tr><th>Buchung</th><th>Mieter</th><th>Zeitraum</th><th>Art</th><th>Miete</th><th>Status</th></tr></thead>
                          <tbody>
                            {d.buchungen.map((b) => {
                              const s = BUCHUNG_STATUS[b.status];
                              return (
                                <tr key={b.id}>
                                  <td><Link to={`/laden/buchungen/${b.id}`}>{b.nummer}</Link></td>
                                  <td><Link to={`/laden/mieter/${b.mieterId}`}>{b.mieterName}</Link> <span className="sc-muted sc-small">{b.mieternummer}</span></td>
                                  <td>{formatTag(b.start)}–{b.ende ? formatTag(b.ende) : "offen"}</td>
                                  <td>{b.art === "abo" ? "Miet-Abo" : b.einheit === "woche" ? "Wochen" : "Monate"}</td>
                                  <td className="sc-num">{formatEuro(b.mieteCent)} / {b.einheit === "woche" ? "Woche" : "Monat"}</td>
                                  <td><StatusBadge status={s.badge}>{s.text}</StatusBadge></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                </>
              }
              aside={
                <>
                  <Card title="Aktueller Mieter">
                    {d.aktuell ? (
                      <div className="sc-row">
                        <span className="sc-avatar">{d.aktuell.mieterName.split(" ").map((t) => t[0]).join("").slice(0, 2).toUpperCase()}</span>
                        <div className="sc-col" style={{ gap: 0 }}>
                          <Link to={`/laden/mieter/${d.aktuell.mieterId}`} className="sc-strong">{d.aktuell.mieterName}</Link>
                          <span className="sc-muted sc-small">Nr. {d.aktuell.mieternummer} · {d.aktuell.status === "reserviert" ? "Vertrag noch offen" : "Hauptmieter"}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="sc-muted">Zurzeit frei{d.kommende[0] ? ` · ab ${formatTag(d.kommende[0].start)} ${d.kommende[0].mieterName}` : ""}.</p>
                    )}
                    {d.kommende.length && d.aktuell ? <p className="sc-muted sc-small" style={{ marginTop: "var(--space-3)" }}>Danach: {d.kommende.map((k) => `${k.mieterName} ab ${formatTag(k.start)}`).join(", ")}</p> : null}
                  </Card>
                  <Card title="Belegung">
                    <Belegungsleiste heute={heute} buchungen={d.buchungen.filter((b) => ["reserviert", "aktiv"].includes(b.status))} />
                  </Card>
                  <LeistungKarte regalId={id} />
                </>
              }
            />
            {bearbeiten ? <RegalFormular regal={d.regal} onClose={() => setBearbeiten(false)} /> : null}
          </>
        );
      }}
    </Seitenzustand>
  );
}

/** Drei Monate ab heute als Monatszeilen mit Belegungsbalken. */
function Belegungsleiste({ heute, buchungen }: { heute: string; buchungen: { id: string; start: string; ende: string | null; status: string; mieterName: string }[] }) {
  const monate = [0, 1, 2].map((i) => {
    const d = new Date(`${heute.slice(0, 7)}-01T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + i);
    const von = d.toISOString().slice(0, 10);
    const e = new Date(d);
    e.setUTCMonth(e.getUTCMonth() + 1);
    e.setUTCDate(0);
    const bis = e.toISOString().slice(0, 10);
    return { label: new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric", timeZone: "UTC" }).format(d), von, bis, tage: e.getUTCDate() };
  });
  return (
    <div className="sc-col">
      {monate.map((m) => (
        <div key={m.von} className="sc-col" style={{ gap: 4 }}>
          <span className="sc-small sc-muted">{m.label}</span>
          <div className="app-belegung-leiste" role="img" aria-label={`Belegung ${m.label}`}>
            {buchungen
              .filter((b) => b.start <= m.bis && (b.ende === null || b.ende >= m.von))
              .map((b) => {
                const s = b.start > m.von ? b.start : m.von;
                const e = b.ende && b.ende < m.bis ? b.ende : m.bis;
                const links = ((Number(s.slice(8, 10)) - 1) / m.tage) * 100;
                const breite = ((Number(e.slice(8, 10)) - Number(s.slice(8, 10)) + 1) / m.tage) * 100;
                return <span key={b.id} className={["app-belegung-balken", b.status === "reserviert" && "is-reserviert"].filter(Boolean).join(" ")} style={{ left: `${links}%`, width: `${breite}%` }} title={`${b.mieterName} ${formatTag(b.start)}–${b.ende ? formatTag(b.ende) : "offen"}`} />;
              })}
          </div>
        </div>
      ))}
      <div className="sc-toolbar">
        <span className="sc-legend"><i style={{ background: "var(--violett)" }} /> Gebucht</span>
        <span className="sc-legend"><i style={{ background: "var(--attention)" }} /> Reserviert, Vertrag offen</span>
        <span className="sc-legend"><i style={{ background: "var(--surface-sunken)", border: "1px solid var(--line)" }} /> Frei</span>
      </div>
    </div>
  );
}

/** Regal-Leistung (E9.5): 12 Monate Umsatz als Balken, Auslastung, Liegezeit, Empfehlung. */
function LeistungKarte({ regalId }: { regalId: string }) {
  const q = useRegalLeistung(regalId);
  return (
    <Card title="Leistung · 12 Monate">
      <Seitenzustand query={q} lines={3}>
        {(l) => {
          const max = Math.max(1, ...l.monate.map((m) => m.umsatzCent));
          return (
            <>
              <div className="app-sparkline" role="img" aria-label={`Umsatz je Monat, zusammen ${formatEuro(l.umsatz12Cent)}`}>
                {l.monate.map((m) => <span key={m.monat} className={m.umsatzCent > 0 ? "is-on" : m.belegtTage === 0 ? "is-leer" : undefined} style={{ height: `${Math.max(4, (m.umsatzCent / max) * 100)}%` }} title={`${m.monat}: ${formatEuro(m.umsatzCent)}, ${m.verkauft} verkauft, ${m.belegtTage}/${m.tage} Tage belegt`} />)}
              </div>
              <Dl rows={[["Umsatz", formatEuro(l.umsatz12Cent)], ["Auslastung", `${l.auslastungProzent} %`], ["Ø Umsatz je belegtem Monat", formatEuro(l.umsatzJeBelegtemMonatCent)], ["Ø Liegezeit verkaufter Artikel", l.verkauft12 ? `${l.liegezeitTage} Tage` : "–"]]} />
              <p className="sc-small" style={{ margin: "var(--space-2) 0 0" }}>{l.empfehlung}</p>
            </>
          );
        }}
      </Seitenzustand>
    </Card>
  );
}
